import { useContext, useEffect } from "react";
import { bugsnagInit } from "../components/bugsnag-client";
import { ErrorBoundary } from "react-error-boundary";
import ErrorPage from "../components/error-page";
import APIContext from "../contexts/APIContext";
import APIContextProvider from "../contexts/APIContext/Provider";

import { Loading } from "@analyticsMD/quil";
import { Sizes } from "../types/loadingSizes";
import type { FC } from "react";

import { ActionTrack } from "../types/Analytics";

import { datadogRum } from "@datadog/browser-rum";
import QCardContent from "../components/list/qcard-content";
import { Box } from "@mui/material";

const Layout: FC = () => {
  const {
    patientQuery,
    dispositionsQuery,
    patientDispositionsQuery,
    configurationQuery,
    intelligenceTextQuery,
    medicallyReadyStatusQuery,
    patientConsiderationsQuery,
    analytics,
    eddQuery,
    sessionExpired,
  } = useContext(APIContext);

  const allQueries = [
    patientQuery,
    dispositionsQuery,
    patientDispositionsQuery,
    configurationQuery,
    intelligenceTextQuery,
    medicallyReadyStatusQuery,
    eddQuery,
    patientConsiderationsQuery,
  ];
  const allQueriesSuccessful = allQueries.every((query) => query.isSuccess);
  const anyQueryErrors = allQueries.some((query) => query.isError);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emrPatientID = urlParams.get("emr_patient_id");
    const userID = urlParams.get("emr_user_id");
    const userName = urlParams.get("emr_user_full_name");
    const userEmail = urlParams.get("emr_user_email");
    const userRole = urlParams.get("emr_user_role");
    const userServiceArea = urlParams.get("emr_user_service_area");
    const userDeptSpeciality = urlParams.get("emr_user_dept_speciality");
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APP_ID,
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: "patient_embed",
      env: process.env.NODE_ENV,

      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      usePartitionedCrossSiteSessionCookie: true,
      useSecureSessionCookie: true,
    });
    datadogRum.setUser({
      id: userID,
      name: userName,
      email: userEmail,
      role: userRole,
      service_area: userServiceArea,
      dept_speciality: userDeptSpeciality,
    });
    const customer_name = window.location.host.split(".")[0];
    datadogRum.setGlobalContextProperty("stack_name", customer_name);
    datadogRum.setGlobalContextProperty("customer", customer_name);
    datadogRum.setGlobalContextProperty("emr_patient_id", emrPatientID);
    datadogRum.startSessionReplayRecording();
  }, []);

  useEffect(() => {
    if (configurationQuery.isSuccess) bugsnagInit(configurationQuery.data);
  }, [configurationQuery.isSuccess, configurationQuery.data]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (allQueriesSuccessful) {
      analytics?.track(ActionTrack.PATIENT_CARD_OPENED, {
        emr_dept_name: urlParams.get("emr_dept_name"),
        emr_dept_npi: urlParams.get("emr_dept_npi"),
        emr_patient_id: patientQuery.data.emr_patient_id,
        emr_hospital_id: patientQuery.data.emr_hospital_id,
        fhir_patient_id: patientQuery.data.fhir_patient_id,
        is_eligible: patientQuery.data.eligibility?.is_eligible,
        solution_class: patientQuery.data.eligibility?.solution_class,
      });
    }
  }, [patientQuery.data, analytics, allQueriesSuccessful]);

  if (!allQueriesSuccessful && !anyQueryErrors) {
    return (
      <Box sx={{ marginTop: "25%" }} data-testid="page">
        <Loading message={"Loading..."} logoSize={Sizes.Medium} />
      </Box>
    );
  }

  if (sessionExpired) {
    return (
      <ErrorPage
        error={new Error("Session expired, try launching patient card again")}
        showActualError={true}
      />
    );
  }

  return <QCardContent />;
};

const PatientEmbedPage: FC = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    <APIContextProvider
      emrPatientID={urlParams.get("emr_patient_id")}
      emrHospitalID={urlParams.get("emr_hospital_id")}
      fhirPatientID={urlParams.get("fhir_patient_id")}
      authContextID={urlParams.get("auth_context_id")}
    >
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Layout />
      </ErrorBoundary>
    </APIContextProvider>
  );
};

export default PatientEmbedPage;
